import { CabinetContextMenuContext } from '@components/map/contextMenu/builders/sensor';
import { useCabinetCommand } from '@hooks/useCabinetCommand';
import {
  getCabinetSectionStatuses,
  getDefenceState,
  isSensorOrSubSensorConfigurationOfType,
  isSensorOrSubSensorStatusOfType,
} from '@utils/sensors/sensors.utils';

import { SensorOrSubSensorConfiguration } from '@/types/sensor/configuration.types';
import {
  AutomatonCabinet,
  AutomatonSegmentStatus,
  AutomatonSensorConfiguration,
  SensorTypeEnum,
  SpecificStateEnum,
  SubSensorTypeEnum,
} from '@/types/sensor/sensors';
import { SensorOrSubSensorStatus } from '@/types/sensor/status.types';

const DEFAULT_CONTEXT: CabinetContextMenuContext = {
  isAckAlarmsLoading: false,
  isAckAlarmsDisabled: true,
  isCabinetConnectionEnabled: false,
  isCabinetConnectionOptionLoading: false,
  isCabinetConnectionOptionDisabled: true,
  isCabinetRepulsionOptionLoading: false,
  isDisableSegmentsRepulsionDisabled: true,
  isDisableSegmentsRepulsionHidden: true,
  isEnableSegmentsRepulsionDisabled: true,
  isEnableSegmentsRepulsionHidden: true,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onAckAlarmsAction: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onCabinetConnectionOptionAction: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onCabinetRepulsionOptionAction: () => {},
};

export function useAutomatonCabinetSensorContextMenu(
  configuration: SensorOrSubSensorConfiguration | null,
  status: SensorOrSubSensorStatus | AutomatonSegmentStatus | null,
  parentSensorConfiguration: SensorOrSubSensorConfiguration | null,
): CabinetContextMenuContext {
  const isCabinetConfiguration = isSensorOrSubSensorConfigurationOfType(
    configuration,
    SubSensorTypeEnum.AUTOMATON_CABINET,
  );
  const isCabinetStatus = isSensorOrSubSensorStatusOfType(status, SubSensorTypeEnum.AUTOMATON_CABINET);
  const hasAutomatonParent = isSensorOrSubSensorConfigurationOfType(
    parentSensorConfiguration,
    SensorTypeEnum.AUTOMATON,
  );

  const useCabinet = useCabinetCommand({
    parentSensorConfiguration: parentSensorConfiguration as AutomatonSensorConfiguration | null,
    cabinetConfiguration: configuration as AutomatonCabinet | null,
  });

  if (!useCabinet.canCommand || !isCabinetConfiguration || !isCabinetStatus || !hasAutomatonParent) {
    return DEFAULT_CONTEXT;
  }

  const sectionStatuses = getCabinetSectionStatuses(status);
  const defenceState = getDefenceState(sectionStatuses);

  const alarmCount = Object.values(status.subSensorStatuses).reduce((acc, curr) => {
    const lowerStatus = curr.lowerSegment?.specificState === SpecificStateEnum.EFFRACTION ? 1 : 0;
    const upperStatus = curr.upperSegment?.specificState === SpecificStateEnum.EFFRACTION ? 1 : 0;
    return acc + lowerStatus + upperStatus;
  }, 0);

  const connection = status.specificState === SpecificStateEnum.SLEEP;

  return {
    isAckAlarmsLoading: useCabinet.isReinitializationCommandLoading,
    isAckAlarmsDisabled: alarmCount === 0,
    onAckAlarmsAction: useCabinet.reinitialize,
    isCabinetConnectionEnabled: connection,
    isCabinetConnectionOptionLoading: useCabinet.isSleepCommandLoading,
    isCabinetConnectionOptionDisabled: !status,
    onCabinetConnectionOptionAction: () => useCabinet.sleep(connection),
    isCabinetRepulsionOptionLoading: useCabinet.isRepulsionCommandLoading,
    isDisableSegmentsRepulsionDisabled: defenceState === 'detection',
    isDisableSegmentsRepulsionHidden: !useCabinet.canDetect,
    isEnableSegmentsRepulsionDisabled: defenceState === 'repulsion',
    isEnableSegmentsRepulsionHidden: !useCabinet.canRepulse,
    onCabinetRepulsionOptionAction: useCabinet.repulse,
  };
}
